import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { Translation } from '../i18n/translation.service';
import { requestLanguage } from '../i18n/language-storage';
import { appEnv } from '../config/app-env';

/** True for a relative URL (not a static asset) or one under the configured API base URL. */
export function isApiRequest(url: string): boolean {
  if (/^\/?assets\//.test(url)) return false;
  if (!/^[a-z][a-z\d+.-]*:|^\/\//i.test(url)) return true;
  const base = appEnv.apiBaseUrl.replace(/\/+$/, '');
  return base.length > 0 && (url === base || url.startsWith(`${base}/`));
}

export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const auth = inject(AuthService);
  const router = inject(Router);
  const translation = inject(Translation);

  const currentLang = requestLanguage(translation.translateService);

  // The bearer goes only to the admin API (relative URLs, which apiBaseUrlInterceptor prefixes, or the API origin),
  // never to a third-party absolute URL.
  const token = isApiRequest(req.url) ? auth.getAccessToken() : null;
  const withAuth = token
    ? req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang
        }
      })
    : req.clone({
        setHeaders: {
          'Accept-Language': currentLang
        }
      });

  return next(withAuth).pipe(
    catchError((err: unknown) => {
      // 401 is handled by `adminAuthRefreshInterceptor` (refresh + retry); only redirect if refresh did not apply.
      if (err instanceof HttpErrorResponse && err.status === 401 && req.headers.has('X-Auth-Retry')) {
        auth.clear();
        void router.navigateByUrl('/login');
      }
      return throwError(() => err);
    })
  );
}

